import i18n from "i18next";
import { initReactI18next } from "react-i18next";

export const DEFAULT_LANGUAGE = "ru";

export const SUPPORTED_LANGUAGES = [
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
  { code: "tj", label: "Тоҷикӣ" },
];

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map(({ code }) => code);
const STORAGE_KEY = "i18nextLng";

/**
 * Словари подключаются динамическим импортом: в стартовый бандл попадает только
 * активный язык. Раньше все три файла переводов (≈230 КБ) грузились сразу,
 * хотя пользователь видит один.
 */
const loaders = {
  ru: () => import("../../locales/ru/translation.json"),
  en: () => import("../../locales/en/translation.json"),
  tj: () => import("../../locales/tj/translation.json"),
};

export function normalizeLanguage(value) {
  const code = String(value ?? "")
    .slice(0, 2)
    .toLowerCase();
  return SUPPORTED_CODES.includes(code) ? code : DEFAULT_LANGUAGE;
}

function detectLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
  } catch {
    // localStorage может быть недоступен (приватный режим) — не критично.
  }

  return normalizeLanguage(navigator.language);
}

async function loadBundle(language) {
  if (i18n.hasResourceBundle(language, "translation")) return;

  const module = await loaders[language]();
  i18n.addResourceBundle(language, "translation", module.default, true, true);
}

/** Смена языка с догрузкой словаря. */
export async function changeLanguage(value) {
  const language = normalizeLanguage(value);

  await loadBundle(language);
  await i18n.changeLanguage(language);

  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Значение просто не сохранится между сессиями.
  }
}

/** Вызывается один раз перед монтированием приложения. */
export async function initI18n() {
  const language = detectLanguage();

  await i18n.use(initReactI18next).init({
    lng: language,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_CODES,
    resources: {},
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });

  await loadBundle(language);

  // Резервный язык нужен для ключей, которые ещё не переведены.
  if (language !== DEFAULT_LANGUAGE) {
    await loadBundle(DEFAULT_LANGUAGE);
  }

  return i18n;
}

export default i18n;
